import * as React from 'react'
import { type VariantProps } from 'class-variance-authority'
import { mtc, TextColors, TypographyStyles } from '../../../utils/style-helpers'
import {
  alertVariants,
  alertTitleStyles,
  alertDescriptionStyles,
} from './styles'
import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { Icon } from '../icon'
import { Text } from '../text'

interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  title?: string
  description?: string
  customIconName?: IconDefinition
  dismissible?: boolean
  children?: React.ReactNode
  permanentDismissalKey?: string
  onClose?: () => void
}

type AlertVariant = NonNullable<AlertProps['variant']>

const iconMap = {
  default: { icon: solid('circle-info'), color: 'moss-200' },
  success: { icon: solid('check'), color: 'green-200' },
  warning: { icon: solid('triangle-exclamation'), color: 'yellow-200' },
  error: { icon: solid('circle-exclamation'), color: 'red-200' },
  priority: { icon: solid('star'), color: 'orange-200' },
}

const colorMap = {
  default: 'neutral-600',
  success: 'green-100',
  warning: 'yellow-100',
  error: 'red-100',
  priority: 'orange-200',
}

export const Alert: React.FC<AlertProps> = ({
  className,
  variant = 'default',
  title,
  description,
  customIconName,
  dismissible = false,
  children,
  permanentDismissalKey,
  onClose = () => {},
  ...props
}) => {
  const [visible, setVisible] = React.useState(
    permanentDismissalKey ? !localStorage.getItem(permanentDismissalKey) : true
  )
  const { icon: defaultIcon, color } = iconMap[variant as AlertVariant]
  const textColor = colorMap[variant as AlertVariant]

  if (!visible) return null

  const handleClose = () => {
    setVisible(false)
    if (permanentDismissalKey) {
      localStorage.setItem(permanentDismissalKey, 'true')
    }
    onClose()
  }

  return (
    <div
      role="alert"
      className={mtc(
        alertVariants({ variant }),
        `${className} flex items-start`
      )}
      {...props}
    >
      <Icon
        icon={customIconName ? customIconName : defaultIcon}
        className="h-4 w-4 mr-4 mt-0.5"
        color={color}
      />
      <div className="flex flex-col flex-grow">
        {title && (
          <Text
            variant={TypographyStyles.LABEL_MD}
            className={mtc(alertTitleStyles({ variant }))}
          >
            {title}
          </Text>
        )}
        {description && (
          <Text
            variant={TypographyStyles.BODY_SM}
            color={textColor as TextColors}
            className={mtc(alertDescriptionStyles())}
          >
            {description}
          </Text>
        )}
        {children}
      </div>
      {dismissible && (
        <Icon
          icon={regular('times')}
          className="h-4 w-4 cursor-pointer"
          onClick={handleClose}
          color="text-neutral-500"
        />
      )}
    </div>
  )
}
Alert.displayName = 'Alert'
